import { collect } from 'collect.js';
import { TAX_BEHAVIOUR } from '../exports/Constants';
import TaxCalculator from './TaxCalculator';

/**
 * Pure subscription / pricing-tier / promo calculations extracted from billing
 * components. The contract-discount surface (applyContractDiscount,
 * discountPrice, discountLineItem, …) lives in ContractDiscountHelpers.js.
 * See tests/services/helpers/PricingHelpers.test.js.
 */

/** Half-up rounding to a fixed number of decimals (cents by default). */
export function roundHalfUp(value, decimals = 2) {
  if (!Number.isFinite(value)) {
    return value;
  }

  const factor = 10 ** decimals;

  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * Compute the tax portion and tax-inclusive (brutto) amount for a subscription
 * price, honouring the inclusive/exclusive tax behaviour.
 *
 * @returns { tax, taxed_amount }
 */
export function computeSubscriptionTax(amount, taxPercentage, taxBehaviour) {
  const calculator = TaxCalculator.make(taxBehaviour);

  return {
    tax: calculator.tax(amount, taxPercentage),
    taxed_amount: calculator.brutto(amount, taxPercentage),
  };
}

/**
 * Resolve the headline unit amount shown for a price: the first tier's amount
 * when tiered, otherwise the gross amount for inclusive tax and the net amount
 * for exclusive tax.
 */
export function resolveUnitAmount(price) {
  if (price.tiers.length > 0) {
    return price.tiers[0].amount;
  }

  return price.tax_behaviour === TAX_BEHAVIOUR.inclusive ? price.taxed_amount : price.amount;
}

/**
 * Lower/upper threshold bounds for a pricing tier. The final tier is unbounded
 * (`to === null` → rendered as "≥ from").
 *
 * @returns { from, to }
 */
export function getTierThresholdBounds(tiers, index) {
  const isLast = index === tiers.length - 1;

  return {
    from: tiers[index].threshold,
    to: isLast ? null : tiers[index + 1].threshold,
  };
}

/** Sum the `discount` field across promo-code components. */
export function sumPromoComponents(components) {
  return collect(components).sum('discount');
}

/**
 * Largest stripe-billing discount across setup packages
 * (internal_invoice_billing_price − stripe_billing_price). 0 when there are none.
 */
export function computeStripeBillingDiscount(setupPackages) {
  const item = collect(setupPackages)
    .sort(
      (a, b) =>
        b.internal_invoice_billing_price -
        b.stripe_billing_price -
        (a.internal_invoice_billing_price - a.stripe_billing_price),
    )
    .first();

  if (!item) {
    return 0;
  }

  return item.internal_invoice_billing_price - item.stripe_billing_price;
}
